"use client";

import React from "react";
import { formatDistanceToNow } from "date-fns";
import { formatCurrency } from "@/lib/utils/crm/status-helpers";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";
import { Badge } from "@/components/ui/badge";
import { SmartUserDisplay, SmartUserChip } from "@/components/shared/smart-display/SmartUserDisplay";
import { SmartContactDisplay, SmartContactChip } from "@/components/shared/smart-display/SmartContactDisplay";
import { ContactMethodActions } from "@/components/shared/ContactMethodActions/ContactMethodActions";
import type { ContactMethodEntityContext } from "@/lib/utils/crm/contact-method-context";
import { Mail, ExternalLink, FileText, Clock, User, Building } from "lucide-react";

/**
 * Render helper for empty values
 */
export function renderEmpty() {
  return <span className="text-gray-400">-</span>;
}

/**
 * Render helper for status badges
 */
export function renderStatusBadge(
  status: string,
  colorMap: Record<string, string> = {}
): React.ReactNode {
  const defaultColors: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    inactive: "bg-card-100 text-gray-800",
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    ...colorMap,
  };

  const color = defaultColors[status] || "bg-card-100 text-gray-800";

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
}

/**
 * Render helper for email links (opens Hub compose modal)
 */
export function renderEmailLink(
  email: string,
  entityContext?: ContactMethodEntityContext
): React.ReactNode {
  if (!email) return renderEmpty();
  return (
    <span className="text-primary flex items-center gap-1 min-w-0">
      <Mail className="h-4 w-4 flex-shrink-0" />
      <ContactMethodActions kind="email" value={email} entityContext={entityContext} />
    </span>
  );
}

/**
 * Render helper for phone links (opens Hub send-text modal)
 */
export function renderPhoneLink(
  phone: string,
  entityContext?: ContactMethodEntityContext
): React.ReactNode {
  if (!phone) return renderEmpty();
  return (
    <ContactMethodActions kind="phone" value={phone} entityContext={entityContext} />
  );
}

/**
 * Render helper for external links
 */
export function renderExternalLink(url: string, label: string = "View"): React.ReactNode {
  if (!url) return renderEmpty();
  return (
    <a
      href={url.startsWith("http") ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:text-primary-dark flex items-center gap-1 text-sm truncate"
    >
      <ExternalLink className="h-4 w-4" />
      {label}
    </a>
  );
}

/**
 * Render helper for currency values
 */
export function renderCurrency(
  amount: number | null | undefined,
  suffix: string = ""
): React.ReactNode {
  if (!amount) return renderEmpty();
  return (
    <div className="font-mono text-green-700">
      {formatCurrency(amount)}
      {suffix}
    </div>
  );
}

/**
 * Render helper for time values
 */
export function renderTime(hours: number | null | undefined): React.ReactNode {
  if (!hours) return renderEmpty();
  return (
    <div className="flex items-center gap-1">
      <Clock className="h-4 w-4 text-gray-400" />
      <span className="font-mono text-card-foreground">{hours}h</span>
    </div>
  );
}

/**
 * Render helper for dates with relative time
 */
export function renderDateWithRelative(date: string | null | undefined): React.ReactNode {
  if (!date) return renderEmpty();
  return (
    <div className="min-w-0">
      <div className="font-medium text-card-foreground">{formatDate(date)}</div>
      <div className="text-sm text-gray-500">
        {formatDistanceToNow(new Date(date), { addSuffix: true })}
      </div>
    </div>
  );
}

/**
 * Render helper for relative time only
 */
export function renderRelativeTime(date: string | null | undefined): React.ReactNode {
  if (!date) return renderEmpty();
  return (
    <div className="text-sm text-gray-600">
      {formatDistanceToNow(new Date(date), { addSuffix: true })}
    </div>
  );
}

/**
 * Render helper for tags/badges list
 */
export function renderTagsList(
  items: string[] | null | undefined,
  maxShow: number = 2
): React.ReactNode {
  if (!items || items.length === 0) return renderEmpty();
  return (
    <div className="flex flex-wrap gap-1">
      {items.slice(0, maxShow).map((item, index) => (
        <span
          key={index}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-800"
        >
          {item}
        </span>
      ))}
      {items.length > maxShow && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-800">
          +{items.length - maxShow}
        </span>
      )}
    </div>
  );
}

/**
 * Render helper for truncated text with title
 */
export function renderTruncatedText(text: string | null | undefined): React.ReactNode {
  if (!text) return renderEmpty();
  return (
    <div className="text-sm text-gray-600 truncate" title={text}>
      {text}
    </div>
  );
}
